import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import React, { ReactNode } from 'react';

import { sectionGridSx, conditionHistoryHelper } from './MainPartEdit.helpers';
import { MainPartEditTouched } from './MainPartEdit.props';
import { SizeProps } from '../../../@types';
import { PartConditionDropdown, PartLocationObject, PartObject } from '../../../@types/part.type';
import { PartType } from '../../../@types/partType.type';
import { Panel } from '../../atoms/Panel';
import { SelectMenu } from '../../atoms/SelectMenu';
import { EditFieldRow } from '../../molecules/EditFieldRow';
import { EditNodePartLiveSearch } from '../../organisms/EditNodePartLiveSearch/EditNodePartLiveSearch';

interface StatusPlacementSectionProps {
  value: PartObject;
  conditions: PartConditionDropdown[];
  partType: PartType;
  partLocation: PartLocationObject;
  canEditCondition: boolean;
  canEditLocation: boolean;
  canEditClientHierarchy: boolean;
  validate: boolean;
  touched: MainPartEditTouched;
  exceptionSlot?: ReactNode;
  duplicatesSlot?: ReactNode;
  onChange: (value: PartObject) => void;
  onTouchCondition: () => void;
  handlePartLocationChange: (value: PartLocationObject) => void;
}

export const StatusPlacementSection: React.FC<StatusPlacementSectionProps> = ({
  value,
  conditions,
  partType,
  partLocation,
  canEditCondition,
  canEditLocation,
  canEditClientHierarchy,
  validate,
  touched,
  exceptionSlot,
  duplicatesSlot,
  onChange,
  onTouchCondition,
  handlePartLocationChange,
}) => {
  const historyHelper = conditionHistoryHelper(value);
  const showConditionError =
    (touched.condition || validate) && (!value.conditionCode || value.conditionCode === '-1');

  return (
    <Panel title="Status & Placement">
      <Box sx={sectionGridSx}>
        <EditFieldRow
          label="Condition"
          required
          htmlFor="part-edit-condition"
          helper={historyHelper}
          error={showConditionError}
        >
          <SelectMenu
            disabled={!canEditCondition}
            required={false}
            size={SizeProps.Small}
            id="part-edit-condition"
            labelId="part-edit-condition-label"
            label="Condition"
            selectedValue={value.conditionCode}
            onChange={(condition) => {
              const item = conditions.find((c) => String(c.value) === String(condition));
              onChange({
                ...value,
                conditionCode: condition,
                conditionName: item?.label ?? '',
              });
              onTouchCondition();
            }}
            items={conditions}
            validate={validate || touched.condition}
            optionalLabelEnabled
          />
        </EditFieldRow>
        <EditFieldRow label="Location" fullWidth>
          <EditNodePartLiveSearch
            isLocation={canEditLocation}
            isClientHierarchy={canEditClientHierarchy}
            partLocation={partLocation}
            isEditable
            handlePartLocationChange={handlePartLocationChange}
            applicableNodeIds={partType?.applicableNodeIds}
            isUpdatePartLocation
          />
        </EditFieldRow>
      </Box>
      {exceptionSlot && (
        <>
          <Divider sx={{ my: 2 }} />
          {exceptionSlot}
        </>
      )}
      {duplicatesSlot && (
        <>
          <Divider sx={{ my: 2 }} />
          {duplicatesSlot}
        </>
      )}
    </Panel>
  );
};
